import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Play,
  Pause,
  RotateCcw,
  RefreshCw,
} from "react-feather";
import { Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getJobQueueAPI,
  pauseQueueAPI,
  resumeQueueAPI,
  retryAllRetryableAPI,
} from "../../../api/api";
import { useInterval } from "../../../Hooks/useInterval";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./JobQueue.module.scss";

// ── Renamed from statusChip → queueStatusBadge to avoid clash with statusChips state ──
function queueStatusBadge(q) {
  if (q.status === "running")
    return <Badge className={styles.badgeRunning}>Running</Badge>;
  if (q.status === "paused")
    return <Badge className={styles.badgePaused}>Paused</Badge>;
  return <Badge className={styles.badgeError}>Error</Badge>;
}

function queueCardClass(q) {
  if (q.status === "paused") return styles.queueCardPaused;
  if (q.status === "error") return styles.queueCardError;
  return "";
}

const STATUS_COLORS = {
  queued: "secondary",
  running: "primary",
  success: "success",
  failed: "danger",
};

export default function JobQueue() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [queues, setQueues] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [retryAllOpen, setRetryAllOpen] = useState(false);
  const [retryingAll, setRetryingAll] = useState(false);

  // ── Filter state ──────────────────────────────────────────
  const fQueue = searchParams.get("queue") || "";
  // BUG FIX: statusChips is the local toggle state; URL param is the initial seed only
  const [statusChips, setStatusChips] = useState(() => {
    const s = searchParams.get("status");
    return s ? s.split(",").filter(Boolean) : [];
  });
  const [fTenant, setFTenant] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // BUG FIX: use a ref to build filters so load() doesn't need it as a dependency
  // which caused an infinite loop when statusChips changed inside load()
  const filtersRef = useRef({});
  filtersRef.current = {
    queue: fQueue || undefined,
    status: statusChips.length ? statusChips.join(",") : undefined,
    tenantSearch: fTenant || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getJobQueueAPI(filtersRef.current);
      setQueues(res.data.data?.queues || []);
      setJobs(res.data.data?.jobs || []);
    } catch (e) {
      setError(e);
      toast.error(e.response?.data?.message || "Failed to load job queue");
    } finally {
      setLoading(false);
    }
  }, []); // no deps — reads latest filters via ref

  // BUG FIX: only seed chips from URL once on mount, not on every searchParams change
  // (the previous effect was re-running on every setSearchParams call)
  const didSeedRef = useRef(false);
  useEffect(() => {
    if (didSeedRef.current) return;
    didSeedRef.current = true;
    const s = searchParams.get("status");
    if (s) setStatusChips(s.split(",").filter(Boolean));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  useInterval(
    () => {
      if (autoRefresh) load();
    },
    autoRefresh ? 15000 : null,
  );

  // ── Queue pause / resume ──────────────────────────────────
  const toggleQueue = async (q) => {
    try {
      if (q.status === "paused") {
        await resumeQueueAPI(q.key);
        toast.success(`${q.name} resumed`);
      } else {
        await pauseQueueAPI(q.key);
        toast.success(`${q.name} paused`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  // ── Status chip toggle ────────────────────────────────────
  const toggleStatusChip = (st) => {
    setStatusChips((prev) =>
      prev.includes(st) ? prev.filter((x) => x !== st) : [...prev, st],
    );
  };

  // ── Reset all filters ─────────────────────────────────────
  const resetFilters = () => {
    setStatusChips([]);
    setFTenant("");
    setDateFrom("");
    setDateTo("");
    setSearchParams({});
  };

  // ── Retry all ─────────────────────────────────────────────
  const handleRetryAll = async () => {
    setRetryingAll(true);
    try {
      const res = await retryAllRetryableAPI();
      toast.success(`Re-queued ${res.data.data?.requeued || 0} job(s)`);
      setRetryAllOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to retry jobs");
    } finally {
      setRetryingAll(false);
    }
  };

  // ── Table config ──────────────────────────────────────────
  const rowStyles = useMemo(
    () => [
      {
        when: (r) => r.status === "failed",
        style: { backgroundColor: "rgba(220, 53, 69, 0.08)" },
      },
      {
        when: (r) => r.status === "success",
        style: { backgroundColor: "rgba(100, 73, 152, 0.08)" },
      },
      { when: (r) => r.status === "running", classNames: [styles.pulseRow] },
    ],
    [],
  );

  const columns = useMemo(
    () => [
      { name: "Job ID", selector: (r) => r.id, sortable: true, width: "120px" },
      { name: "Queue", selector: (r) => r.queue, sortable: true },
      { name: "Tenant", selector: (r) => r.tenantCode, sortable: true },
      { name: "Type", selector: (r) => r.type, sortable: true },
      {
        name: "Created",
        selector: (r) => new Date(r.createdAt).toLocaleString(),
        sortable: true,
        grow: 1,
      },
      {
        name: "Started",
        selector: (r) =>
          r.startedAt ? new Date(r.startedAt).toLocaleString() : "—",
        sortable: true,
      },
      {
        name: "Status",
        selector: (r) => r.status,
        sortable: true,
        cell: (r) => (
          <Badge bg={STATUS_COLORS[r.status] ?? "secondary"}>{r.status}</Badge>
        ),
      },
      {
        name: "Duration",
        selector: (r) => (r.durationMs != null ? `${r.durationMs} ms` : "—"),
      },
      {
        name: "Actions",
        ignoreRowClick: true,
        cell: (r) => (
          <Button
            as={Link}
            size="sm"
            variant="outline-primary"
            className={styles.actionButton}
            to={platformUrl(`jobs/${r.id}`)}
          >
            View detail
          </Button>
        ),
      },
    ],
    [],
  );

  // ── Loading / error states ────────────────────────────────
  if (loading && !queues.length && !jobs.length) {
    return (
      <div>
        <PageHeader
          title="Job queue"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Jobs" },
          ]}
        />
        <LoadingSkeleton rows={6} cols={3} />
      </div>
    );
  }

  if (error && !queues.length) {
    return (
      <div>
        <PageHeader
          title="Job queue"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Jobs" },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load queue"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div className={styles.jobQueuePage}>
      <PageHeader
        title="Job queue"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Jobs" },
        ]}
        secondaryActions={[
          {
            label: autoRefresh ? "Auto refresh: On" : "Auto refresh: Off",
            variant: autoRefresh ? "primary" : "outline-secondary",
            onClick: () => setAutoRefresh((v) => !v),
            icon: autoRefresh ? <RefreshCw size={16} /> : undefined,
          },
          {
            label: "Retry center",
            variant: "outline-secondary",
            onClick: () => navigate(platformUrl("jobs/retry")),
          },
        ]}
        primaryAction={{
          label: retryingAll ? "Retrying..." : "Retry all retryable",
          onClick: () => setRetryAllOpen(true),
          disabled: retryingAll,
          icon: retryingAll ? <Spinner size="sm" /> : <RotateCcw size={16} />,
        }}
      />

      {/* ── Queue cards ── */}
      <Row className="g-4 mb-4">
        {queues.map((q) => (
          <Col md={6} xl={3} key={q.key}>
            <Card className={`${styles.queueCard} ${queueCardClass(q)}`}>
              <Card.Body>
                <div className={styles.queueCardHeader}>
                  <h4 className={styles.queueTitle}>{q.name}</h4>
                  {/* BUG FIX: was statusChip(q) — now queueStatusBadge(q) */}
                  {queueStatusBadge(q)}
                </div>
                <div className={styles.queueStats}>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Depth</span>
                    <span className={styles.statValue}>{q.depth} jobs</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Throughput</span>
                    <span className={styles.statValue}>
                      {q.throughputPerMin} jobs/min
                    </span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Success rate</span>
                    <span className={styles.statValue}>{q.successRate}%</span>
                  </div>
                </div>
                <div className={styles.queueCardActions}>
                  <Button
                    size="sm"
                    variant={q.status === "paused" ? "success" : "warning"}
                    onClick={() => toggleQueue(q)}
                    className={styles.actionBtn}
                  >
                    {q.status === "paused" ? (
                      <Play size={14} />
                    ) : (
                      <Pause size={14} />
                    )}
                    <span>{q.status === "paused" ? "Resume" : "Pause"}</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    className={styles.viewFailuresBtn}
                    onClick={() => {
                      // BUG FIX: sync both URL and local chip state together
                      setStatusChips(["failed"]);
                      setFTenant("");
                      setSearchParams({ queue: q.key, status: "failed" });
                    }}
                  >
                    View failures
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Filter toolbar ── */}
      <div className={styles.toolbar}>
        <Row className="g-3 align-items-end">
          <Col md={6} lg={3}>
            <Form.Label className={styles.filterLabel}>Queue</Form.Label>
            <Form.Select
              value={fQueue}
              onChange={(e) => {
                const v = e.target.value;
                // BUG FIX: preserve status in URL when changing queue
                const params = {};
                if (v) params.queue = v;
                if (statusChips.length) params.status = statusChips.join(",");
                setSearchParams(params);
              }}
              className={styles.filterSelect}
            >
              <option value="">All queues</option>
              <option value="teams-sync">teams-sync</option>
              <option value="google-sync">google-sync</option>
            </Form.Select>
          </Col>
          <Col md={6} lg={3}>
            <Form.Label className={styles.filterLabel}>
              Tenant search
            </Form.Label>
            <Form.Control
              value={fTenant}
              onChange={(e) => setFTenant(e.target.value)}
              placeholder="Code or name"
              className={styles.filterInput}
            />
          </Col>
          <Col md={12} lg={4}>
            <Form.Label className={styles.filterLabel}>Status</Form.Label>
            <div className={styles.statusChips}>
              {["running", "queued", "success", "failed"].map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => toggleStatusChip(st)}
                  className={`${styles.statusChip} ${statusChips.includes(st) ? styles.statusChipActive : ""}`}
                >
                  {st}
                </button>
              ))}
            </div>
          </Col>
          <Col md={6} lg={2}>
            <Form.Label className={styles.filterLabel}>From</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={styles.filterInput}
            />
          </Col>
          <Col md={6} lg={2}>
            <Form.Label className={styles.filterLabel}>To</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className={styles.filterInput}
            />
          </Col>
          <Col xs={12} className="mt-3 d-flex gap-2">
            <Button className={styles.applyBtn} onClick={load}>
              Apply filters
            </Button>
            <Button
              variant="outline-secondary"
              className={styles.resetBtn}
              onClick={resetFilters}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </div>

      {/* ── Jobs table ── */}
      <DataTableWrapper
        columns={columns}
        data={jobs}
        loading={loading}
        keyField="id"
        showSearch={false}
        customTableProps={{ conditionalRowStyles: rowStyles }}
      />

      <ConfirmModal
        isOpen={retryAllOpen}
        onClose={() => setRetryAllOpen(false)}
        onConfirm={handleRetryAll}
        title="Retry all retryable?"
        message="Failed jobs will be moved back to the queue for processing."
        confirmLabel={retryingAll ? "Retrying..." : "Retry all"}
        danger
        confirmDisabled={retryingAll}
      />
    </div>
  );
}
